import isObject from 'lodash/isObject';

import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import { getAction } from '../actions/getters';
import { getService } from '../apps/getters';
import {
	getNode,
	getGlobalWorkflowDataWithKey,
	useNodeEdges as nodeEdgesGetter,
	getPreviousStepDataForNodeByUUID,
	useNodeIncomingEdges as nodeIncomingEdgesGetter,
	useNodeOutgoingEdges as nodeOutgoingEdgesGetter,
	useIsRootNode as useIsRootNodeGetter,
	isEndRunWidget as isEndRunWidgetGetter,
	isWidget as isWidgetGetter,
} from '../nodes/getters';
import { rootNode as rootNodeGetter, selectedWorkflowId } from '../selectedWorkflow/getters';
import { nodesDataSorted } from '../workflows/getters';
import { flattenPreviousSteps, buildRootStepPreviousStepsObject } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
const buildSelectedNodeGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations
export const editNode = buildSelectedNodeGetterObject('editNode');
export const loadingExtraFields = buildSelectedNodeGetterObject('loadingExtraFields');
export const editNodeParams = buildSelectedNodeGetterObject('editNodeParams');
export const editNodeAction = buildSelectedNodeGetterObject('editNodeAction');
export const sideBarState = buildSelectedNodeGetterObject('sideBarState');
export const editNodeAppInfo = buildSelectedNodeGetterObject('editNodeAppInfo');
export const nodeType = buildSelectedNodeGetterObject('nodeType');
export const isWidget = buildSelectedNodeGetterObject('isWidget');
export const isEndRun = buildSelectedNodeGetterObject('isEndRun');
export const isEndRunWidget = buildSelectedNodeGetterObject('isEndRunWidget');
export const isWebhook = buildSelectedNodeGetterObject('isWebhook');
export const isScheduledWebhook = buildSelectedNodeGetterObject('isScheduledWebhook');
export const isSubFlow = buildSelectedNodeGetterObject('isSubFlow');
export const isTriggerNode = buildSelectedNodeGetterObject('isTriggerNode');
export const isBranches = buildSelectedNodeGetterObject('isBranches');
export const isBranchesConsolidateDataNode = buildSelectedNodeGetterObject('isBranchesConsolidateDataNode');
export const isEmailTemplates = buildSelectedNodeGetterObject('isEmailTemplates');
export const editNodeParamsId = buildSelectedNodeGetterObject('editNodeParamsId');
export const isTemp = buildSelectedNodeGetterObject('isTemp');
export const isFlowTreeDragging = buildSelectedNodeGetterObject('isFlowTreeDragging');
export const flatPreviousSteps = buildSelectedNodeGetterObject('flatPreviousSteps');
export const isSearchOrCreate = buildSelectedNodeGetterObject('isSearchOrCreate');
export const isNodeDataLoading = buildSelectedNodeGetterObject('isNodeDataLoading');
export const editNodeOriginal = buildSelectedNodeGetterObject('editNodeOriginal');
export const isMiniApp = buildSelectedNodeGetterObject('isMiniApp');
export const isTestingProfile = buildSelectedNodeGetterObject('isTestingProfile');
export const isSendNotification = buildSelectedNodeGetterObject('isSendNotification');
export const notification = buildSelectedNodeGetterObject('notification');
export const previousStepsDataByNodeLocation = buildSelectedNodeGetterObject('previousStepsDataByNodeLocation');
export const isValueFromAutofill = buildSelectedNodeGetterObject('isValueFromAutofill');
export const autofillError = buildSelectedNodeGetterObject('autofillError');
export const isAutofillLoading = buildSelectedNodeGetterObject('isAutofillLoading');
export const autofillSuggestions = buildSelectedNodeGetterObject('autofillSuggestions');
export const useNodeEdges = buildSelectedNodeGetterObject('useNodeEdges');
export const useNodeIncomingEdges = buildSelectedNodeGetterObject('useNodeIncomingEdges');
export const useNodeOutgoingEdges = buildSelectedNodeGetterObject('useNodeOutgoingEdges');
export const editNodeId = buildSelectedNodeGetterObject('editNodeId');
export const useIsRootNode = buildSelectedNodeGetterObject('useIsRootNode');

export const REGULAR_BLOCK = 'regularBlock';

const getters = {
	[editNode.getter]: (state) => state.editNode,
	[isNodeDataLoading.getter]: (state) => state.isNodeDataLoading,
	[loadingExtraFields.getter]: (state) => state.loadingExtraFields,
	[editNodeParams.getter]: (state) => state.editNodeParams.filter((i) => isObject(i)),
	[sideBarState.getter]: (state) => state.sideBarState,
	[editNodeId.getter]: (state) => state.editNode?.id,
	[useNodeEdges.getter]: (state, getters) => getters[nodeEdgesGetter.fullPath](getters[editNodeId.getter]),
	[useNodeIncomingEdges.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[nodeIncomingEdgesGetter.fullPath](getters[editNodeId.getter]),
	[useNodeOutgoingEdges.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[nodeOutgoingEdgesGetter.fullPath](getters[editNodeId.getter]),

	[editNodeAppInfo.getter]: (state, getters, rootState, rootGetters) => {
		if (!state.editNode) {
			return;
		}
		const info = rootGetters[getService.fullPath](state.editNode.service);
		return info;
	},
	[useIsRootNode.getter]: (state, getters, rootState, rootGetters) =>
		getters[editNodeId.getter] ? rootGetters[useIsRootNodeGetter.fullPath](getters[editNodeId.getter]) : false,
	[nodeType.getter]: (state, getters) => {
		if (!state.editNode) {
			return REGULAR_BLOCK;
		}
		if (getters[isWidget.getter]) {
			return 'widget';
		} else if (getters[isSendNotification.getter]) {
			return 'notification';
		} else if (getters[isBranches.getter] || getters[isBranchesConsolidateDataNode.getter]) {
			return 'branches';
		} else if (getters[isEmailTemplates.getter] && 1 < 0) {
			return 'emailTemplates';
		} else {
			return REGULAR_BLOCK;
		}
	},
	[isWidget.getter]: (state, getters, rootState, rootGetters) => rootGetters[isWidgetGetter.fullPath](state.editNode?.id),
	[isEndRun.getter]: (state) => WorkflowNodeService.isEndRun(state.editNode),
	[isEndRunWidget.getter]: (state, getters, rootState, rootGetters) => rootGetters[isEndRunWidgetGetter.fullPath](state.editNode?.id),
	[isWebhook.getter]: (state) => state.editNode && WorkflowNodeService.isWebhook(state.editNode),
	[isScheduledWebhook.getter]: (state) => state.editNode && WorkflowNodeService.isScheduledWebhook(state.editNode),
	[isSubFlow.getter]: (state) => state.editNode && WorkflowNodeService.isSubflow(state.editNode),
	[isTriggerNode.getter]: (state, getters) => state.editNode && getters[useIsRootNode.getter],
	[isEmailTemplates.getter]: (state) => state.editNode && WorkflowNodeService.isEmailTemplates(state.editNode),
	[isBranches.getter]: (state, getters, rootState, rootGetters) =>
		state.editNode &&
		WorkflowNodeService.isBranches(state.editNode) &&
		!WorkflowNodeService.isConsolidateDataNode(state.editNode, rootGetters[getAction.fullPath]),
	[isBranchesConsolidateDataNode.getter]: (state, getters, rootState, rootGetters) =>
		state.editNode && WorkflowNodeService.isConsolidateDataNode(state.editNode, rootGetters[getAction.fullPath]),
	[isSendNotification.getter]: (state) => state.editNode && WorkflowNodeService.isSendNotification(state.editNode),
	[editNodeAction.getter]: (state, getters, rootState, rootGetters) => rootGetters[getAction.fullPath](state.editNode?.action),

	[editNodeParamsId.getter]: (state) => state.editNodeParamsId,
	[isTemp.getter]: (state) => state.editNode?.temp && state.editNode.id.toString().includes('temp'),
	[isFlowTreeDragging.getter]: (state) => state.isFlowTreeDragging,
	[previousStepsDataByNodeLocation.getter]: (state, getters, rootState, rootGetters) => {
		const selectedNode = getters[editNode.getter];
		const nodesByLocation = [];
		const sortedNodes = rootGetters[nodesDataSorted.fullPath](selectedNode.workflow);
		if (sortedNodes) {
			for (const node of sortedNodes) {
				if (node.id === selectedNode.id) {
					break;
				}
				nodesByLocation.push(node); // question - do we want to include the current node or not? (currently not included)
			}
		}
		return {
			...rootGetters[getGlobalWorkflowDataWithKey.fullPath](rootGetters[selectedWorkflowId.fullPath]),
			...Object.values(nodesByLocation).reduce(
				(allPreviousSteps, node) => ({
					...allPreviousSteps,
					[node.uuid]: rootGetters[getPreviousStepDataForNodeByUUID.fullPath](node.uuid),
				}),
				{}
			),
		};
	},
	[flatPreviousSteps.getter]: (state, getters, rootState, rootGetters) => {
		const previousSteps = rootGetters[previousStepsDataByNodeLocation.fullPath];
		const rootNode = rootGetters[rootNodeGetter.fullPath];
		let rootStep = {};
		if (rootNode) {
			rootStep = buildRootStepPreviousStepsObject(rootNode, rootGetters[getPreviousStepDataForNodeByUUID.fullPath](rootNode.uuid));
		}
		const flatPreviousSteps = flattenPreviousSteps(Object.assign({}, { ...previousSteps }, { ...rootStep }));
		return flatPreviousSteps;
	},
	[isSearchOrCreate.getter]: (state) => state.editNode.node_type === 'search_or_create',
	[editNodeOriginal.getter]: (state, getters, rootState, rootGetters) => rootGetters[getNode.fullPath](getters[editNode.getter]?.id),
	[isMiniApp.getter]: (state) => state.editNode?.params_map && !!state.editNode?.params_map?.mini_app_type,
	[isTestingProfile.getter]: (state) => state.testingProfile,
	[notification.getter]: (state) => state.editNode.notification,
	[isAutofillLoading.getter]: (state) => state.isAutofillLoading,
	[autofillError.getter]: (state) => state.autofillError,
	[isValueFromAutofill.getter]:
		(state) =>
		({ paramKey, value }) =>
			Boolean(state.autofillSuggestions?.[paramKey]?.includes(value)),
	[autofillSuggestions.getter]: (state) => state.autofillSuggestions,
};

export default getters;
